import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  API_BASE_URL,
  ApiClient,
  type AppInfo,
  type CarProject,
  type ChatSession,
  type InviteCreateResult,
  type InviteInfo,
  type KnownCar,
  type TokenPair,
} from "./api/client";
import { streamChat, type ChatEvent } from "./api/sse";
import { ChatLayout } from "./components/ChatLayout";
import { LoginScreen } from "./components/LoginScreen";
import { RegisterScreen } from "./components/RegisterScreen";
import {
  authStateFromTokenPair,
  clearAuthState,
  loadAuthState,
  saveAuthState,
  shouldRefreshSoon,
  type AuthState,
} from "./state/auth";
import {
  mergePanels,
  panelsFromToolEnd,
  placeholderPanelsFromHistory,
  type CanvasPanel,
} from "./state/canvas";
import {
  addToolEvent,
  messageFromPersisted,
  type ChatMessage,
} from "./state/chat";

const APP_ID = "dote";
const SESSION_STORAGE_KEY = "dote-web.activeSessionId.v1";
const CAR_STORAGE_KEY = "dote-web.activeCarSlug.v1";
const CANVAS_OPEN_STORAGE_KEY = "dote-web.canvasOpen.v1";

const UNASSIGNED_SLUG = "__unassigned__";

function loadActiveCarSlug(): string | null {
  const raw = localStorage.getItem(CAR_STORAGE_KEY);
  if (raw === null || raw === "") {
    return null;
  }
  return raw === UNASSIGNED_SLUG ? null : raw;
}

function saveActiveCarSlug(slug: string | null): void {
  localStorage.setItem(CAR_STORAGE_KEY, slug ?? UNASSIGNED_SLUG);
}

export default function App() {
  const [auth, setAuth] = useState<AuthState | null>(() => loadAuthState());
  const [apps, setApps] = useState<AppInfo[]>([]);
  const [projects, setProjects] = useState<CarProject[]>([]);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(() =>
    localStorage.getItem(SESSION_STORAGE_KEY),
  );
  const [activeCarSlug, setActiveCarSlugState] = useState<string | null>(() =>
    loadActiveCarSlug(),
  );
  const [knownCars, setKnownCars] = useState<KnownCar[]>([]);
  const [knownCarsLoading, setKnownCarsLoading] = useState(false);
  const [knownCarsError, setKnownCarsError] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [canvasOpen, setCanvasOpen] = useState(
    () => localStorage.getItem(CANVAS_OPEN_STORAGE_KEY) !== "0",
  );
  const [canvasPanels, setCanvasPanels] = useState<CanvasPanel[]>([]);
  const [addCarOpen, setAddCarOpen] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteCode, setInviteCode] = useState<string | null>(() => parseInviteRoute().code);
  const [inviteEmail, setInviteEmail] = useState<string>(() => parseInviteRoute().email);
  const [inviteInfo, setInviteInfo] = useState<InviteInfo | null>(null);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Generation counter to guard async loadMessages against being overwritten
  // by a stale fetch. Every place that mutates `messages` from "outside" the
  // stream (session switch, send-message optimistic insert) bumps this token
  // so any in-flight loadMessages knows its result is no longer authoritative.
  const messagesGenRef = useRef(0);

  const setActiveCarSlug = useCallback((slug: string | null) => {
    setActiveCarSlugState(slug);
    saveActiveCarSlug(slug);
  }, []);

  const logout = useCallback(() => {
    abortRef.current?.abort();
    clearAuthState();
    localStorage.removeItem(SESSION_STORAGE_KEY);
    localStorage.removeItem(CAR_STORAGE_KEY);
    setAuth(null);
    setProjects([]);
    setSessions([]);
    setActiveSessionId(null);
    setActiveCarSlugState(null);
    setMessages([]);
    setCanvasPanels([]);
    setError(null);
    setIsStreaming(false);
  }, []);

  const refreshTokens = useCallback(async (): Promise<string | null> => {
    if (!auth?.refreshToken) {
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/v1/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: auth.refreshToken }),
      });
      if (!response.ok) {
        return null;
      }
      const pair = (await response.json()) as TokenPair;
      const nextAuth = authStateFromTokenPair(pair);
      saveAuthState(nextAuth);
      setAuth(nextAuth);
      return nextAuth.accessToken;
    } catch {
      return null;
    }
  }, [auth?.refreshToken]);

  const api = useMemo(
    () =>
      new ApiClient({
        getAccessToken: () => auth?.accessToken ?? null,
        refreshTokens,
        onUnauthorized: logout,
      }),
    [auth?.accessToken, logout, refreshTokens],
  );

  const activeSession = useMemo(
    () => sessions.find((session) => session.id === activeSessionId) ?? null,
    [activeSessionId, sessions],
  );

  const appName = apps.find((app) => app.id === APP_ID)?.name || "车大师";

  const activeProjectName = useMemo(() => {
    if (activeSession?.car_slug) {
      const fromProjects = projects.find((p) => p.car_slug === activeSession.car_slug);
      if (fromProjects?.car_name) {
        return fromProjects.car_name;
      }
      const fromKnown = knownCars.find((c) => c.slug === activeSession.car_slug);
      if (fromKnown?.name) {
        return fromKnown.name;
      }
      return activeSession.car_slug;
    }
    if (activeCarSlug) {
      const fromProjects = projects.find((p) => p.car_slug === activeCarSlug);
      if (fromProjects?.car_name) {
        return fromProjects.car_name;
      }
      const fromKnown = knownCars.find((c) => c.slug === activeCarSlug);
      if (fromKnown?.name) {
        return fromKnown.name;
      }
      return activeCarSlug;
    }
    return "未归类";
  }, [activeSession, activeCarSlug, projects, knownCars]);

  useEffect(() => {
    api.listApps().then(setApps).catch(() => setApps([]));
  }, [api]);

  useEffect(() => {
    if (!inviteCode) {
      setInviteInfo(null);
      return;
    }

    setInviteLoading(true);
    setError(null);
    api
      .getInvite(inviteCode)
      .then((invite) => {
        setInviteInfo(invite);
        if (invite.email) {
          setInviteEmail(invite.email);
        }
      })
      .catch((err) => setError(errorMessage(err)))
      .finally(() => setInviteLoading(false));
  }, [api, inviteCode]);

  useEffect(() => {
    if (!auth) {
      return;
    }
    if (shouldRefreshSoon(auth)) {
      void refreshTokens();
    }
  }, [auth, refreshTokens]);

  useEffect(() => {
    if (!auth) {
      return;
    }

    // Only run on initial login (or hard reload). Token refresh must NOT
    // retrigger session reload — that used to cascade into loadMessages and
    // disable the composer between turns.
    void loadProjects();
    void loadSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth?.user.id]);

  useEffect(() => {
    messagesGenRef.current += 1;
    if (!auth || !activeSessionId) {
      setMessages([]);
      setCanvasPanels([]);
      return;
    }

    localStorage.setItem(SESSION_STORAGE_KEY, activeSessionId);
    setMessages([]);
    setCanvasPanels([]);
    void loadMessages(activeSessionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSessionId]);

  useEffect(() => {
    localStorage.setItem(CANVAS_OPEN_STORAGE_KEY, canvasOpen ? "1" : "0");
  }, [canvasOpen]);

  async function handleLogin(email: string, password: string) {
    setLoginLoading(true);
    setError(null);
    try {
      const pair = await api.login(email, password);
      const nextAuth = authStateFromTokenPair(pair);
      saveAuthState(nextAuth);
      setAuth(nextAuth);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoginLoading(false);
    }
  }

  async function handleRegister(input: {
    code: string;
    email: string;
    password: string;
    displayName: string;
  }) {
    setRegisterLoading(true);
    setError(null);
    try {
      const pair = await api.redeemInvite({
        code: input.code,
        email: input.email,
        password: input.password,
        displayName: input.displayName,
      });
      const nextAuth = authStateFromTokenPair(pair);
      saveAuthState(nextAuth);
      setAuth(nextAuth);
      clearInviteRoute();
      setInviteCode(null);
      setInviteInfo(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setRegisterLoading(false);
    }
  }

  async function loadProjects() {
    setProjectsLoading(true);
    try {
      const next = await api.listProjects(APP_ID);
      setProjects(next);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setProjectsLoading(false);
    }
  }

  async function loadSessions() {
    setSessionsLoading(true);
    setError(null);
    try {
      const nextSessions = await api.listSessions(APP_ID);
      setSessions(nextSessions);

      // Only re-derive activeSessionId when the current selection is invalid
      // (first login, deleted session, or a session id from a prior account).
      // Never silently hop to nextSessions[0] between turns.
      const currentId = activeSessionId;
      const currentStillValid =
        currentId && nextSessions.some((session) => session.id === currentId);
      if (currentStillValid) {
        // Sync activeCarSlug with whatever the active session is bound to.
        const current = nextSessions.find((s) => s.id === currentId);
        if (current && current.car_slug !== activeCarSlug) {
          setActiveCarSlug(current.car_slug ?? null);
        }
        return;
      }

      const remembered = localStorage.getItem(SESSION_STORAGE_KEY);
      const slugFilter = activeCarSlug;
      const candidates = slugFilter
        ? nextSessions.filter((s) => (s.car_slug ?? null) === slugFilter)
        : nextSessions;
      const nextActive =
        candidates.find((session) => session.id === remembered)?.id ||
        candidates[0]?.id ||
        nextSessions[0]?.id ||
        null;

      if (nextActive) {
        setActiveSessionId(nextActive);
        const picked = nextSessions.find((s) => s.id === nextActive);
        if (picked) {
          setActiveCarSlug(picked.car_slug ?? null);
        }
      } else if (nextSessions.length === 0) {
        // Brand-new user; do not auto-create a session — wait for them to add
        // a car. This keeps activeCarSlug honest (no orphan empty session
        // bound to "未归类" cluttering the sidebar).
        setActiveSessionId(null);
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSessionsLoading(false);
    }
  }

  async function ensureKnownCars(): Promise<void> {
    if (knownCars.length > 0 || knownCarsLoading) {
      return;
    }
    setKnownCarsLoading(true);
    setKnownCarsError(null);
    try {
      const cars = await api.listKnownCars();
      setKnownCars(cars);
    } catch (err) {
      setKnownCarsError(errorMessage(err));
    } finally {
      setKnownCarsLoading(false);
    }
  }

  async function loadMessages(sessionId: string) {
    const myGen = messagesGenRef.current;
    setMessagesLoading(true);
    setError(null);
    try {
      const persisted = await api.listMessages(APP_ID, sessionId);
      // If another action (session switch, optimistic send) bumped the gen
      // counter while we were fetching, our snapshot is stale — abort the
      // overwrite. This was the root cause of the "second turn never appears"
      // bug: a fresh-session load completed AFTER sendMessage had already
      // optimistically inserted user+assistant rows, wiping them out and
      // leaving the stream events landing on a non-existent message id.
      if (messagesGenRef.current !== myGen) {
        return;
      }
      const hydrated = persisted.map(messageFromPersisted);
      setMessages(hydrated);

      // Replay any persisted tool summaries as stale panel placeholders. The
      // raw tool output isn't stored (only id + title + status), so each
      // panel renders a "工具结果已过期" hint and the user can re-run.
      const placeholders: CanvasPanel[] = [];
      for (const message of hydrated) {
        if (message.role !== "assistant" || message.toolEvents.length === 0) {
          continue;
        }
        const summaries = message.toolEvents.map((e) => ({
          id: e.id,
          title: e.title,
          kind: e.kind,
          status: e.status === "error" ? "error" : "completed",
        }));
        placeholders.push(
          ...placeholderPanelsFromHistory(summaries, message.createdAt),
        );
      }
      setCanvasPanels(placeholders);
    } catch (err) {
      if (messagesGenRef.current === myGen) {
        setError(errorMessage(err));
      }
    } finally {
      if (messagesGenRef.current === myGen) {
        setMessagesLoading(false);
      }
    }
  }

  async function createSession(input: { carSlug?: string | null; title?: string } = {}) {
    setSessionsLoading(true);
    setError(null);
    try {
      const carSlug = input.carSlug !== undefined ? input.carSlug : activeCarSlug;
      const session = await api.createSession(APP_ID, {
        title: input.title || "新对话",
        carSlug,
      });
      setSessions((current) => [session, ...current]);
      // Invalidate any in-flight loadMessages tied to the previous session;
      // the activeSessionId change below will trigger a fresh load anyway.
      messagesGenRef.current += 1;
      setActiveSessionId(session.id);
      setActiveCarSlug(session.car_slug ?? null);
      localStorage.setItem(SESSION_STORAGE_KEY, session.id);
      setDrawerOpen(false);
      setMessages([]);
      setCanvasPanels([]);
      // Optimistically bump the project bucket so the sidebar reflects the
      // new conversation immediately; an authoritative refetch happens on the
      // next loadProjects pass (e.g. after the first send).
      setProjects((current) => bumpProjectCount(current, session.car_slug, knownCars));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSessionsLoading(false);
    }
  }

  async function deleteSession(sessionId: string) {
    if (!window.confirm("删除这个会话？")) {
      return;
    }
    setError(null);
    try {
      await api.deleteSession(APP_ID, sessionId);
      const nextSessions = sessions.filter((session) => session.id !== sessionId);
      setSessions(nextSessions);
      void loadProjects();
      if (activeSessionId === sessionId) {
        const slugFilter = activeCarSlug;
        const candidates = slugFilter
          ? nextSessions.filter((s) => (s.car_slug ?? null) === slugFilter)
          : nextSessions;
        const nextActive = candidates[0]?.id ?? null;
        setActiveSessionId(nextActive);
        if (nextActive) {
          localStorage.setItem(SESSION_STORAGE_KEY, nextActive);
        } else {
          localStorage.removeItem(SESSION_STORAGE_KEY);
          setMessages([]);
          setCanvasPanels([]);
        }
      }
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  async function sendMessage(prompt: string) {
    const sessionId = activeSessionId;
    if (!sessionId || isStreaming) {
      return;
    }

    const userMessage: ChatMessage = {
      id: `local-user-${Date.now()}`,
      role: "user",
      text: prompt,
      createdAt: new Date().toISOString(),
      thoughts: [],
      toolEvents: [],
    };
    const assistantId = `local-assistant-${Date.now()}`;
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: "assistant",
      text: "",
      createdAt: new Date().toISOString(),
      thoughts: [],
      toolEvents: [],
      isStreaming: true,
    };

    // Invalidate any in-flight loadMessages so its setMessages can't wipe
    // out the optimistic insert below. The "second turn vanishes" bug came
    // from exactly this race when creating a session + sending right away.
    messagesGenRef.current += 1;
    setMessages((current) => [...current, userMessage, assistantMessage]);
    setIsStreaming(true);
    setError(null);

    const abortController = new AbortController();
    abortRef.current = abortController;

    try {
      await streamChat({
        api,
        appId: APP_ID,
        sessionId,
        prompt,
        signal: abortController.signal,
        onEvent: (event) => {
          if (event.type === "rebind") {
            const newAcpId =
              typeof event.acp_session_id === "string" ? event.acp_session_id : null;
            setSessions((current) =>
              current.map((session) =>
                session.id === sessionId
                  ? { ...session, acp_session_id: newAcpId }
                  : session,
              ),
            );
            return;
          }
          applyStreamEvent(assistantId, event);
        },
      });
      // Update last_message_at locally; do NOT refetch the whole session list
      // (that used to silently switch the active session between turns).
      const now = new Date().toISOString();
      setSessions((current) =>
        current.map((session) =>
          session.id === sessionId ? { ...session, last_message_at: now } : session,
        ),
      );
    } catch (err) {
      if (abortController.signal.aborted) {
        applyStreamEvent(assistantId, { type: "cancelled" });
      } else {
        setError(errorMessage(err));
        applyStreamEvent(assistantId, { type: "error", message: errorMessage(err) });
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
      setMessages((current) =>
        current.map((message) =>
          message.id === assistantId ? { ...message, isStreaming: false } : message,
        ),
      );
    }
  }

  function applyStreamEvent(messageId: string, event: ChatEvent) {
    if (event.type === "tool_end") {
      // Panel extraction must never break message rendering; a single
      // malformed tool payload used to silently freeze the whole turn
      // because the subsequent setMessages call below was skipped.
      try {
        const newPanels = panelsFromToolEnd(event);
        if (newPanels.length > 0) {
          setCanvasPanels((current) => mergePanels(current, newPanels));
        }
      } catch (err) {
        console.warn("[canvas] failed to extract panels from tool_end", err);
      }
    }

    setMessages((current) =>
      current.map((message) => {
        if (message.id !== messageId) {
          return message;
        }

        if (event.type === "agent_chunk") {
          return { ...message, text: message.text + (typeof event.text === "string" ? event.text : "") };
        }
        if (event.type === "agent_thought") {
          return {
            ...message,
            thoughts: [...message.thoughts, typeof event.text === "string" ? event.text : ""],
          };
        }
        if (event.type === "tool_start" || event.type === "tool_progress" || event.type === "tool_end") {
          return { ...message, toolEvents: addToolEvent(message.toolEvents, event) };
        }
        if (event.type === "error") {
          return { ...message, text: message.text + `\n\n[错误] ${event.message || "stream_error"}` };
        }
        if (event.type === "cancelled") {
          return { ...message, text: message.text || "已停止。", isStreaming: false };
        }

        return message;
      }),
    );
  }

  function stopStreaming() {
    abortRef.current?.abort();
  }

  function selectSession(sessionId: string) {
    setActiveSessionId(sessionId);
    const picked = sessions.find((s) => s.id === sessionId);
    if (picked) {
      setActiveCarSlug(picked.car_slug ?? null);
    }
    setDrawerOpen(false);
  }

  function selectProject(carSlug: string | null) {
    setActiveCarSlug(carSlug);
    const candidates = carSlug
      ? sessions.filter((s) => (s.car_slug ?? null) === carSlug)
      : sessions.filter((s) => s.car_slug === null);
    if (candidates.length > 0) {
      setActiveSessionId(candidates[0].id);
      localStorage.setItem(SESSION_STORAGE_KEY, candidates[0].id);
    }
  }

  function createSessionForCar(carSlug: string | null) {
    void createSession({ carSlug });
  }

  async function openAddCarDialog() {
    setAddCarOpen(true);
    await ensureKnownCars();
  }

  function closeAddCarDialog() {
    setAddCarOpen(false);
    setKnownCarsError(null);
  }

  function pickCar(car: KnownCar) {
    setAddCarOpen(false);
    setActiveCarSlug(car.slug);
    // Disable the composer immediately so a too-fast user (or test driver)
    // can't fire a prompt into the previous session while the new one is
    // still being created. This was the "second turn vanishes" root cause:
    // the optimistic insert ran against the OLD session id, then the new
    // session's useEffect wiped messages before the stream could render.
    messagesGenRef.current += 1;
    setActiveSessionId(null);
    setMessages([]);
    setCanvasPanels([]);
    // Optimistically register the project so the sidebar reflects the picked
    // car immediately, even before its first session exists.
    setProjects((current) => ensureProjectRow(current, car));
    void createSession({ carSlug: car.slug, title: `${car.name} · 新对话` });
  }

  async function createInvite(email: string): Promise<InviteCreateResult> {
    return api.createInvite({ email, expiresInDays: 30, sendEmail: true });
  }

  function openInviteCode(code: string) {
    if (!code) {
      return;
    }
    const normalized = code.trim().toUpperCase();
    window.history.pushState({}, "", `/invite/${encodeURIComponent(normalized)}`);
    setInviteCode(normalized);
    setInviteEmail("");
    setError(null);
  }

  function backToLogin() {
    clearInviteRoute();
    setInviteCode(null);
    setInviteEmail("");
    setInviteInfo(null);
    setError(null);
  }

  if (!auth) {
    if (inviteCode) {
      return (
        <RegisterScreen
          code={inviteCode}
          initialEmail={inviteEmail}
          invite={inviteInfo}
          isLoading={inviteLoading}
          isSubmitting={registerLoading}
          error={error}
          onRegister={handleRegister}
          onBackToLogin={backToLogin}
        />
      );
    }

    return (
      <LoginScreen
        onLogin={handleLogin}
        onInviteCode={openInviteCode}
        isLoading={loginLoading}
        error={error}
      />
    );
  }

  return (
    <ChatLayout
      user={auth.user}
      appName={appName}
      projects={projects}
      sessions={sessions}
      activeCarSlug={activeCarSlug}
      activeSession={activeSession}
      activeProjectName={activeProjectName}
      messages={messages}
      drawerOpen={drawerOpen}
      canvasOpen={canvasOpen}
      canvasPanels={canvasPanels}
      addCarOpen={addCarOpen}
      knownCars={knownCars}
      knownCarsLoading={knownCarsLoading}
      knownCarsError={knownCarsError}
      projectsLoading={projectsLoading}
      sessionsLoading={sessionsLoading}
      messagesLoading={messagesLoading}
      isStreaming={isStreaming}
      error={error}
      onDrawerOpen={() => setDrawerOpen(true)}
      onDrawerClose={() => setDrawerOpen(false)}
      onCanvasOpen={() => setCanvasOpen(true)}
      onCanvasClose={() => setCanvasOpen(false)}
      onSelectProject={selectProject}
      onSessionSelect={selectSession}
      onCreateSessionForCar={createSessionForCar}
      onDeleteSession={(sessionId) => void deleteSession(sessionId)}
      onAddCarOpen={() => void openAddCarDialog()}
      onAddCarClose={closeAddCarDialog}
      onPickCar={pickCar}
      onSend={sendMessage}
      onCreateInvite={createInvite}
      onStop={stopStreaming}
      onLogout={logout}
    />
  );
}

function bumpProjectCount(
  current: CarProject[],
  carSlug: string | null,
  knownCars: KnownCar[],
): CarProject[] {
  const slug = carSlug ?? null;
  const now = new Date().toISOString();
  const idx = current.findIndex((p) => p.car_slug === slug);
  if (idx >= 0) {
    const next = [...current];
    next[idx] = {
      ...next[idx],
      session_count: next[idx].session_count + 1,
      last_message_at: now,
    };
    return next;
  }
  const name =
    (slug ? knownCars.find((c) => c.slug === slug)?.name : null) ||
    slug ||
    "未归类";
  return [
    ...current,
    {
      car_slug: slug,
      car_name: name,
      session_count: 1,
      last_message_at: now,
    },
  ];
}

function ensureProjectRow(
  current: CarProject[],
  car: KnownCar,
): CarProject[] {
  if (current.some((p) => p.car_slug === car.slug)) {
    return current;
  }
  return [
    ...current,
    {
      car_slug: car.slug,
      car_name: car.name,
      session_count: 0,
      last_message_at: null,
    },
  ];
}

function parseInviteRoute(): { code: string | null; email: string } {
  const pathMatch = window.location.pathname.match(/^\/invite\/([^/]+)$/);
  const params = new URLSearchParams(window.location.search);
  const code = pathMatch?.[1] || params.get("invite") || params.get("code");
  return {
    code: code ? decodeURIComponent(code).trim().toUpperCase() : null,
    email: params.get("email") || "",
  };
}

function clearInviteRoute(): void {
  if (window.location.pathname.startsWith("/invite") || window.location.search) {
    window.history.replaceState({}, "", "/");
  }
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  return "unknown_error";
}
